/**
 * FuturesMarketView — `/futures/market` list rebuilt to mirror `/sales/contract/list`.
 *
 * Access evidence:
 *   - `form_frmFuturesMainSubFormMarket.txt`   (parent — no New/Edit/Delete toolbar)
 *   - `form_frmFuturesQryMarket.txt`           (15 visible columns)
 *   - `query_FuturesQryMarket.txt`             (SQL: GROUP BY exchange,contract → max(timing))
 *
 * Migrated to /table-style template (2026-05-26):
 *   - FilterBar + FilterButton (1 nút Search) + Columns
 *   - useColumnFilters + per-column ▽ Excel-style (auto-detect: exchange/contract/mo
 *     đều dưới 30 distinct → checkboxes; timing → date; numeric → number)
 *   - Drop local search/exchange/contract/mo/date state — qua per-column filter
 *   - Read-only (Market không có CRUD theo Access parent form)
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'
import { useMemo, useState, type CSSProperties } from 'react'

import { FUTURES_ALL_ROWS_PAGE_SIZE, useGetMarketPricesQuery } from '../../store/api/futuresControlApi'
import BrandState from '../../components/BrandState'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import { useFuturesColumns } from './useFuturesColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'

const STORAGE_COLS_MARKET = 'futures.market'

interface MarketRow {
  contract: string | null
  last: number | null
  change: number | null
  prevOpen: number | null
  high: number | null
  low: number | null
  prev: number | null
  volume: number | null
  oi: number | null
  timing: string | null
  mo: number | string | null
  exchange: string | null
  symbol: string | null
  statusMarketPrice: string | null
  dateUpdate: string | null
  personUpdate: string | null
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof MarketRow
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** Column order + captions match Access `frmFuturesQryMarket` (form_frmFuturesQryMarket.txt). */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract', label: 'Contract', filter: 'checkboxes' },
  { key: 'last', label: 'Last', kind: 'number', align: 'right' },
  { key: 'change', label: 'Change', kind: 'number', align: 'right' },
  { key: 'prevOpen', label: 'Prev Open', kind: 'number', align: 'right' },
  { key: 'high', label: 'High', kind: 'number', align: 'right' },
  { key: 'low', label: 'Low', kind: 'number', align: 'right' },
  { key: 'prev', label: 'Prev', kind: 'number', align: 'right' },
  { key: 'volume', label: 'Volume', kind: 'number', align: 'right' },
  { key: 'oi', label: 'OI', kind: 'number', align: 'right' },
  { key: 'timing', label: 'Timing', kind: 'date' },
  { key: 'mo', label: 'Mo', filter: 'checkboxes' },
  { key: 'exchange', label: 'Exchange', filter: 'checkboxes' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

/** Cột freeze khi scroll ngang — chỉ cột định danh. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contract'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  contract: 130,
}

/** Numeric columns whose sum is not meaningful — exclude from the totals row. */
const NON_SUM_TOTAL_KEYS = new Set<keyof MarketRow>([
  'last',
  'change',
  'prevOpen',
  'high',
  'low',
  'prev',
])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

const sumNumericColumn = (rows: MarketRow[], key: keyof MarketRow): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const v = row[key]
    if (typeof v !== 'number' || !Number.isFinite(v)) continue
    total += v
    count += 1
  }
  return count > 0 ? total : null
}

const cellValue = (row: MarketRow, col: ColumnDef): string => {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  return String(v)
}

interface FuturesMarketViewProps {
  /** True when rendered inside `FuturesControlPage` (default for `/futures/market`). */
  embedded?: boolean
}

export default function FuturesMarketView({ embedded: _embedded }: FuturesMarketViewProps = {}) {
  const columnsCtrl = useFuturesColumns({
    mode: 'market',
    allColumns: TABLE_COLUMNS,
    storageKey: STORAGE_COLS_MARKET,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.market')
  const {
    selectedId: selectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof MarketRow
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [sortState, setSortState] = useState<TableSortState<keyof MarketRow> | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const { data, isLoading, isFetching, error } = useGetMarketPricesQuery({
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  })

  const baseRows = useMemo<MarketRow[]>(
    () => (data?.items as MarketRow[] | undefined) ?? [],
    [data?.items],
  )

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS, (row, key) => row[key]),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key)).map((col) => [
          col.key,
          sumNumericColumn(filteredRows, col.key),
        ]),
      ),
    [filteredRows],
  )

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () =>
      colPopover
        ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
        : null,
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const rowKey = (r: MarketRow) =>
    `${r.exchange ?? ''}|${r.contract ?? ''}|${r.timing ?? ''}`

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`


  const toggleSort = (key: keyof MarketRow) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Contract, exchange, symbol..."
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load market prices.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Market prices"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Market prices found"
              description="Try adjusting the filters above, or wait for the next price tick."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const k = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(k)
                    const w = getWidth(k) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[k] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={k}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          // Cho phép bôi đen + copy tên cột (header); click-sort vẫn chạy.
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(k)}
                          onClick={(position) =>
                            setColPopover({ key: col.key, position })
                          }
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(k, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const k = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(k)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(k) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[k] : undefined)
                    return (
                      <TableCell
                        key={k}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: MarketRow) =>
                visibleColumns.map((col) => {
                  const k = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(k)
                  const val = cellValue(row, col)
                  const w = getWidth(k) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[k] : undefined)
                  const wrap = getWidth(k) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={k}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={k}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1800, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const r = Number.isFinite(idx) ? rows[idx] : null
                  const key = r ? rowKey(r) : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={key != null && key === selectedKey}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (key != null) toggleSelectKey(key)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
